import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  Output
} from '@angular/core';
import { events } from '@utils/events';

import throttle from 'lodash-es/throttle';

import { ScrollService } from './scroll.service';

@Directive({
  selector: '[appInfiniteScroll]',
  standalone: false
})
export class InfiniteScrollDirective implements AfterViewInit, OnDestroy {
  // The selector of an element in which scrolling is contained
  @Input() appInfiniteScrollSelector: string;
  // Event name, triggering which will cause infinite scroll callback to be called again to check whether more items should be shown
  @Input() appInfiniteScrollEvent: string;
  // How close the bottom of the element must be to the bottom of the screen to trigger
  @Input('appInfiniteScrollThreshold') threshold;
  // To improve performance or just disable infinite scrolling
  @Input('appInfiniteScrollDisabled') disabled;

  @Output() scrolled = new EventEmitter<void>();

  private element: HTMLElement;
  private listener;
  private unsubscribeFromEvent;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private elRef: ElementRef<HTMLElement>,
    private ngZone: NgZone
  ) {}

  ngAfterViewInit() {
    const threshold = this.threshold ? parseInt(this.threshold, 10) : 150;

    this.listener = throttle(
      () => {
        if (!this.disabled && this.element.offsetParent && ScrollService.getScrollBottom(this.element) <= threshold) {
          // ngZone.run on scroll reduces performance a lot,
          // so let's avoid when possible
          this.ngZone.run(() => {
            this.scrolled.emit();
          });
        }
      },
      200,
      { leading: true }
    );

    setTimeout(() => {
      if (this.appInfiniteScrollSelector) {
        this.element = this.document.querySelector(this.appInfiniteScrollSelector);
      } else {
        this.element = this.elRef.nativeElement;
      }

      if (this.appInfiniteScrollEvent) {
        this.unsubscribeFromEvent = events.on(this.appInfiniteScrollEvent, this.listener);
      }

      this.element.addEventListener('scroll', this.listener);
      this.listener();
    }, 0);
  }

  ngOnDestroy() {
    this.element.removeEventListener('scroll', this.listener);
    if (this.unsubscribeFromEvent) {
      this.unsubscribeFromEvent();
    }
  }
}
